import React, { useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup, Marker } from "react-simple-maps";
import { scaleLog } from "d3-scale";
import { geoCentroid } from "d3-geo";
// Use local GeoJSON file that has correct borders (same as Dashboard)
const geoUrl = "/world-countries.json";
import { useTheme } from "@/hooks/useTheme";
import { useLanguage } from "@/hooks/useLanguage";

interface WorldMapProps {
  countryStats: Record<string, { count: number; hasNewAttacks: boolean }>;
  selectedCountry: string | null;
  onSelectCountry: (countryCode: string | null) => void;
}

import { getCountryISO2, countryCodeMap } from "@/utils/countryMapping";

// ...

const WorldMap: React.FC<WorldMapProps> = ({ countryStats, selectedCountry, onSelectCountry }) => {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const [tooltipContent, setTooltipContent] = useState<{
    name: string;
    count: number;
    x: number;
    y: number;
  } | null>(null);

  // ...

  // Calculate max count for color scaling
  const maxCount = useMemo(() => {
    const counts = Object.values(countryStats).map(s => s.count);
    return Math.max(...counts, 0);
  }, [countryStats]);

  // Dynamic color scale based on theme using Logarithmic scale
  // Dark mode: White -> Red
  // Light mode: Black -> Red
  const colorScale = useMemo(() => {
    const startColor = theme === 'dark' ? '#ffffff' : '#000000';
    const endColor = '#ef4444'; // Red-500

    // Log scale needs domain > 0
    return scaleLog<string>()
      .domain([1, Math.max(2, maxCount)]) // Ensure domain max > min
      .range([startColor, endColor])
      .clamp(true);
  }, [theme, maxCount]);

  const getLocalizedCountryName = (code: string) => {
    try {
      const regionNames = new Intl.DisplayNames([language], { type: 'region' });
      return regionNames.of(code) || code;
    } catch (e) {
      return code;
    }
  };

  const handleCountryClick = (geo: any) => {
    const countryName = geo.properties.name;
    let countryCode = getCountryISO2(countryName);

    // Fallback to ISO3 from geo.id if name lookup fails
    if (!countryCode && geo.id && geo.id.length === 3) {
      const iso3 = geo.id;
      countryCode = Object.keys(countryCodeMap).find(key => countryCodeMap[key] === iso3);
    }

    if (countryCode) {
      if (selectedCountry === countryCode) {
        onSelectCountry(null); // Deselect if already selected
      } else {
        onSelectCountry(countryCode);
      }
    } else {
      onSelectCountry(null);
    }
  };

  return (
    <div className="relative w-full mb-8">
      {/* Tooltip */}
      {tooltipContent && (
        <div
          className="fixed z-50 px-3 py-2 text-sm font-medium text-white bg-black/80 rounded shadow-lg pointer-events-none transform -translate-x-1/2 -translate-y-full"
          style={{ left: tooltipContent.x, top: tooltipContent.y - 10 }}
        >
          <p>{tooltipContent.name}</p>
          <p className="text-xs text-gray-300">
            {tooltipContent.count} {language === 'pl'
              ? (tooltipContent.count === 1 ? 'atak' : tooltipContent.count > 1 && tooltipContent.count < 5 ? 'ataki' : 'ataków')
              : (tooltipContent.count === 1 ? 'attack' : 'attacks')}
          </p>
          {tooltipContent.count === 0 && (
            <p className="text-xs text-green-400 mt-1">
              {language === 'pl' ? 'Nie odnotowano ataków ransomware' : 'No ransomware attacks recorded'}
            </p>
          )}
        </div>
      )}

      <div className="w-full h-[400px] md:h-[500px] bg-secondary/20 rounded-xl overflow-hidden border border-border">
        <ComposableMap
          projectionConfig={{ rotate: [-10, 0, 0], scale: 210 }}
          className="w-full h-full"
        >
          <ZoomableGroup center={[0, 0]} zoom={1}>
            <Geographies geography={geoUrl}>
              {({ geographies }) => (
                <>
                  {geographies.map((geo) => {
                    const countryName = geo.properties.name;
                    // Try to get country code from name, fallback to ISO3 from geo.id
                    let countryCode = getCountryISO2(countryName);

                    // If name mapping failed, try using the ISO3 code from geo.id directly
                    if (!countryCode && geo.id && geo.id.length === 3) {
                      // Find ISO2 for this ISO3
                      const iso3 = geo.id;
                      countryCode = Object.keys(countryCodeMap).find(key => countryCodeMap[key] === iso3);
                    }

                    const stats = countryCode ? countryStats[countryCode] : undefined;
                    const count = stats?.count || 0;
                    const isSelected = countryCode === selectedCountry;

                    // Determine fill color
                    let fillColor;
                    if (count === 0) {
                      fillColor = theme === 'dark' ? "#333333" : "#e5e7eb"; // Default gray for 0 attacks
                    } else {
                      fillColor = colorScale(count);
                    }

                    if (isSelected) {
                      fillColor = "#FFD700"; // Selected color (Gold)
                    }

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onClick={() => handleCountryClick(geo)}
                        onMouseEnter={(evt) => {
                          const { clientX, clientY } = evt;
                          setTooltipContent({
                            name: countryCode ? getLocalizedCountryName(countryCode) : countryName,
                            count: count,
                            x: clientX,
                            y: clientY
                          });
                        }}
                        onMouseMove={(evt) => {
                          if (tooltipContent) {
                            const { clientX, clientY } = evt;
                            setTooltipContent(prev => prev ? ({ ...prev, x: clientX, y: clientY }) : null);
                          }
                        }}
                        onMouseLeave={() => {
                          setTooltipContent(null);
                        }}
                        style={{
                          default: {
                            fill: fillColor,
                            outline: "none",
                            stroke: theme === 'dark' ? "#1f2937" : "#9ca3af",
                            strokeWidth: 0.5,
                            cursor: "pointer",
                            transition: "all 250ms"
                          },
                          hover: {
                            fill: isSelected
                              ? "#EAB308" // Darker yellow/gold on hover
                              : "#ef4444", // red-500
                            outline: "none",
                            stroke: "#FFFFFF",
                            strokeWidth: 0.5,
                            cursor: "pointer",
                            transition: "all 250ms"
                          },
                          pressed: {
                            fill: isSelected ? "#CA8A04" : "#991b1b", // Darker gold or red
                            outline: "none",
                            stroke: "#FFFFFF",
                            strokeWidth: 0.5,
                          },
                        }}
                      />
                    );
                  })}
                  {geographies.map((geo) => {
                    const countryName = geo.properties.name;
                    const countryCode = getCountryISO2(countryName);
                    const stats = countryCode ? countryStats[countryCode] : undefined;
                    const hasNewAttacks = stats?.hasNewAttacks || false;

                    if (hasNewAttacks) {
                      const centroid = geoCentroid(geo);
                      return (
                        <Marker key={`${geo.rsmKey}-marker`} coordinates={centroid}>
                          {/* Ripple effect for new attacks */}
                          <g style={{ pointerEvents: 'none' }}>
                            <circle r={4} fill="#ef4444" className="animate-ping opacity-75" />
                            <circle r={8} className="animate-ripple-ring stroke-red-500" style={{ animationDelay: '0s' }} />
                            <circle r={12} className="animate-ripple-ring stroke-red-500" style={{ animationDelay: '0.8s' }} />
                          </g>
                        </Marker>
                      );
                    }
                    return null;
                  })}
                </>
              )
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-col items-center gap-4">
        <div className="w-full max-w-md flex items-center gap-4">
          <span className="text-xs text-muted-foreground">1</span>
          <div
            className="flex-1 h-3 rounded-full"
            style={{
              background: `linear-gradient(to right, ${theme === 'dark' ? '#ffffff' : '#000000'}, #ef4444)`
            }}
          ></div>
          <span className="text-xs text-muted-foreground">{Math.max(1, maxCount)}</span>
        </div>

        <div className="flex items-center gap-6 text-xs text-muted-foreground">
          <span>
            {language === 'pl' ? 'Liczba ataków ransomware' : 'Ransomware attacks count'}
          </span>

          <div className="flex items-center gap-2">
            <div className="relative w-4 h-4 flex items-center justify-center">
              <div className="absolute w-2 h-2 bg-red-500 rounded-full animate-ping opacity-75"></div>
              <div className="absolute w-full h-full border border-red-500 rounded-full opacity-50"></div>
            </div>
            <span>
              {language === 'pl' ? 'Ataki z dzisiaj' : 'Attacks from today'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorldMap;
